import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import type { CustomerTierBulletToken } from '@sleed/bizcommerce-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { serverMessage } from '~/lib/api/server-error';
import { useCustomerTierBulletMutations, useCustomerTierBullets } from '~/hooks/use-customer-tier-bullets';

const MAX_TEXT = 500;

/**
 * Privilege bullets of an existing tier (BW-8339): edited inline and saved immediately through the bullet endpoints,
 * independent of the tier form's Save. Shown on the storefront tier profile, active ones in sort order.
 */
export function TierBullets({ tierId }: { tierId: number }) {
	const { t } = useTranslation();
	const { data: bullets = [], isLoading } = useCustomerTierBullets(tierId);
	const { create, update, remove } = useCustomerTierBulletMutations(tierId);
	const [draft, setDraft] = useState('');

	const run = async (action: Promise<unknown>) => {
		try {
			await action;
		} catch (err) {
			toast.error(serverMessage(err));
		}
	};

	const add = async () => {
		const text = draft.trim();
		if (!text) return;
		const nextSort = bullets.reduce((max, b) => Math.max(max, b.si ?? 0), 0) + 1;
		await run(create.mutateAsync({ tx: text, si: nextSort, act: true, iv: true }));
		setDraft('');
	};

	return (
		<div className="flex flex-col gap-2">
			<div className="text-sm font-medium text-foreground">{t('customers.tiers.bullets.title')}</div>
			{isLoading ? null : bullets.length === 0 ? (
				<p className="text-xs text-foreground-subtle">{t('customers.tiers.bullets.empty')}</p>
			) : (
				<ul className="flex flex-col gap-2">
					{bullets.map(b => (
						<BulletRow
							key={b.id}
							bullet={b}
							onSave={next => run(update.mutateAsync(next))}
							onDelete={() => run(remove.mutateAsync(b.id!))}
						/>
					))}
				</ul>
			)}
			<div className="flex items-center gap-2">
				<Input
					value={draft}
					maxLength={MAX_TEXT}
					placeholder={t('customers.tiers.bullets.placeholder')}
					onChange={e => setDraft(e.target.value)}
					onKeyDown={e => {
						if (e.key === 'Enter') {
							e.preventDefault();
							void add();
						}
					}}
				/>
				<Button type="button" variant="outline" size="sm" onClick={() => void add()} disabled={!draft.trim() || create.isPending}>
					<Plus className="size-4" />
					{t('customers.tiers.bullets.add')}
				</Button>
			</div>
		</div>
	);
}

function BulletRow({
	bullet,
	onSave,
	onDelete
}: {
	bullet: CustomerTierBulletToken;
	onSave: (next: CustomerTierBulletToken) => Promise<unknown>;
	onDelete: () => Promise<unknown>;
}) {
	const { t } = useTranslation();
	const [text, setText] = useState(bullet.tx ?? '');

	return (
		<li className="flex items-center gap-2">
			<Input
				type="number"
				className="w-16"
				defaultValue={bullet.si ?? 0}
				aria-label={t('customers.tiers.columns.sort')}
				onBlur={e => {
					const si = Number(e.target.value);
					if (si !== (bullet.si ?? 0)) void onSave({ ...bullet, si });
				}}
			/>
			<Input
				value={text}
				maxLength={MAX_TEXT}
				onChange={e => setText(e.target.value)}
				onBlur={() => {
					if (text.trim() && text !== bullet.tx) void onSave({ ...bullet, tx: text.trim() });
				}}
			/>
			<Switch
				checked={bullet.act ?? false}
				aria-label={t('customers.tiers.columns.active')}
				onCheckedChange={act => void onSave({ ...bullet, act })}
			/>
			<Button type="button" variant="ghost" size="sm" onClick={() => void onDelete()} aria-label={t('common.delete')}>
				<Trash2 className="size-4" />
			</Button>
		</li>
	);
}
